import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CancelRounded from '@mui/icons-material/CancelRounded';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';

import {
    mapMarketingLeadApiToRow,
    updateRestaurantLeadByMarketingAPI,
    bulkUpdateShouldContactByZipcodeAPI,
} from '../../context-api/api-services/MarketingLead.service';
import { useMarketingLeadData } from '../../context-api/marketing-lead/MarketingLeadDataContext';
import {
    getMarketingLeadPageCache,
    invalidateMarketingLeadPageCache,
    setMarketingLeadPageCache,
} from '../../utils/marketingLeadPageCache';
import {
    COLORS,
    MARKETING_LEAD_SEARCH_DEBOUNCE_MS,
} from './marketingLeadConstants';
import {
    storeManagementOutlinedSelectSx as outlinedSelectSx,
    storeManagementMenuPaperSx as menuPaperSx,
    storeManagementMenuItemSx as menuItemSx,
} from '../../utils/storeManagementSelectSx';
import useTwilioVoice from '../../hooks/useTwilioVoice';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import { isTeamMemberWithRouteRestrictions } from '../../utils/regBranchRouteAccess';

// ── Split components ──────────────────────────────────────────────────────────
import { StatusBadge, AddressCell } from './components/PillBadge';
import { StatusLogsTable } from './components/StatusLogsTable';
import { CallRecordingPanel } from './components/CallRecordingPanel';
import { UpdateLeadModal, CURRENT_STATUS_OPTIONS } from './components/UpdateLeadModal';
import { CallSmsModal } from './components/CallSmsModal';
import { BulkZipModal } from './components/BulkZipModal';

// ── Split utilities ───────────────────────────────────────────────────────────
import {
    formatLastRefresh,
    formatLogTime,
    normalizeToE164,
    parseStatusValueForApi,
    formatMarketingUpdateError,
} from './utils/marketingLeadHelpers';

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_TAB_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'not_onboarded_yet', label: 'Not onboarded' },
    { value: 'onboarding_in_progress', label: 'In progress' },
    { value: 'onboarded', label: 'Onboarded' },
    { value: 'onboarding_failed', label: 'Failed' },
    { value: 'contacted_restaurant', label: 'Contacted' },
    { value: 'meeting_scheduled', label: 'Meeting scheduled' },
];

// ── Main Component ────────────────────────────────────────────────────────────

const MarketingLead = () => {
    const { regBranchSession } = useContext(GlobalStateDataContext);
    const canDeleteRecording = !isTeamMemberWithRouteRestrictions(regBranchSession);

    const { restaurantLeadsData, fetchRestaurantLeads, markRestaurantLeadsSatisfiedWithoutFetch } =
        useMarketingLeadData();

    // ── List state ──────────────────────────────────────────────────────────────
    const [rows, setRows] = useState([]);
    const [totalLeads, setTotalLeads] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [textSearch, setTextSearch] = useState('');
    const [zipCodeSearch, setZipCodeSearch] = useState('');
    const [debouncedTextSearch, setDebouncedTextSearch] = useState('');
    const [debouncedZipCodeSearch, setDebouncedZipCodeSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [smsSentFilter, setSmsSentFilter] = useState('all');
    const [shouldContactFilter, setShouldContactFilter] = useState('all');
    const [loadError, setLoadError] = useState(null);
    const [lastRefreshAt, setLastRefreshAt] = useState(null);
    const [listRefreshNonce, setListRefreshNonce] = useState(0);

    // ── Row expansion / menu ────────────────────────────────────────────────────
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [accordionTab, setAccordionTab] = useState(0);
    const [rowMenu, setRowMenu] = useState({ anchorEl: null, row: null });

    // ── Modal state ─────────────────────────────────────────────────────────────
    const [actionModal, setActionModal] = useState({ open: false, type: 'call', row: null });
    const [updateModal, setUpdateModal] = useState({
        open: false, row: null,
        currentStatus: 'not_onboarded_yet',
        marketingNotes: '', smsSent: false,
        submitting: false, error: '',
    });
    const [bulkContactModal, setBulkContactModal] = useState({
        open: false, zip: '', shouldContact: true,
        submitting: false, error: '', result: null,
    });

    // ── Twilio voice ────────────────────────────────────────────────────────────
    const voice = useTwilioVoice('agent_regional');
    const twilio_call_row_ref = useRef(null);
    const twilio_call_sid_ref = useRef(null);

    useEffect(() => {
        if (voice.active_call_sid) {
            twilio_call_sid_ref.current = voice.active_call_sid;
        }
    }, [voice.active_call_sid]);

    // ── Derived ─────────────────────────────────────────────────────────────────
    const loading = restaurantLeadsData.loading;
    const skipSearchPageResetRef = useRef(true);
    const hasActiveFilters =
        textSearch || zipCodeSearch || statusFilter !== 'all' || smsSentFilter !== 'all' || shouldContactFilter !== 'all';

    const listCacheFilters = useMemo(
        () => ({
            q: debouncedTextSearch,
            zipcode: debouncedZipCodeSearch,
            smsSent: smsSentFilter,
            currentStatus: statusFilter,
            shouldContact: shouldContactFilter,
        }),
        [debouncedTextSearch, debouncedZipCodeSearch, smsSentFilter, statusFilter, shouldContactFilter]
    );

    // ── Debounced search ────────────────────────────────────────────────────────
    const commitDebouncedSearch = useMemo(
        () => debounce((value) => setDebouncedTextSearch(String(value ?? '').trim()), MARKETING_LEAD_SEARCH_DEBOUNCE_MS),
        []
    );

    useEffect(() => {
        commitDebouncedSearch(textSearch);
        return () => { commitDebouncedSearch.cancel(); };
    }, [textSearch, commitDebouncedSearch]);

    const commitDebouncedZipSearch = useMemo(
        () => debounce((value) => setDebouncedZipCodeSearch(String(value ?? '').trim()), MARKETING_LEAD_SEARCH_DEBOUNCE_MS),
        []
    );

    useEffect(() => {
        commitDebouncedZipSearch(zipCodeSearch);
        return () => { commitDebouncedZipSearch.cancel(); };
    }, [zipCodeSearch, commitDebouncedZipSearch]);

    useEffect(() => {
        if (skipSearchPageResetRef.current) { skipSearchPageResetRef.current = false; return; }
        setPage(1);
    }, [debouncedTextSearch, debouncedZipCodeSearch, statusFilter, smsSentFilter, shouldContactFilter]);

    // ── Data fetching ───────────────────────────────────────────────────────────
    useEffect(() => {
        const cached = getMarketingLeadPageCache(page, limit, listCacheFilters);
        if (cached) {
            markRestaurantLeadsSatisfiedWithoutFetch();
            setRows(cached.rows);
            setTotalLeads(cached.totalLeads);
            setLastRefreshAt(cached.refreshedAt);
            setLoadError(null);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoadError(null);
            const res = await fetchRestaurantLeads({
                q: debouncedTextSearch || undefined,
                zipcode: debouncedZipCodeSearch || undefined,
                page,
                limit,
                sms_sent: smsSentFilter !== 'all' ? smsSentFilter === 'true' : undefined,
                current_status: statusFilter !== 'all' ? statusFilter : undefined,
                should_contact: shouldContactFilter !== 'all' ? shouldContactFilter === 'true' : undefined,
            });
            if (cancelled) return;

            if (res.hasError) {
                setLoadError(res.message || 'Failed to load marketing leads.');
                setRows([]); setTotalLeads(0); setLastRefreshAt(null);
                return;
            }

            const payload = res.data || {};
            if (payload.success !== true) {
                setLoadError(payload.CustomMessage || payload.message || 'Could not load marketing leads.');
                setRows([]); setTotalLeads(0); setLastRefreshAt(null);
                return;
            }

            const list = Array.isArray(payload.data) ? payload.data : [];
            const pagination = payload.pagination || {};
            const total = Number(
                pagination.totalDocs ?? pagination.totalCount ??
                payload.totalDocs ?? payload.totalCount ??
                payload.total_leads ?? list.length
            ) || list.length;

            const mapped = list.map((item, i) => mapMarketingLeadApiToRow(item, i));
            const refreshedAt = Date.now();
            setMarketingLeadPageCache(page, limit, { rows: mapped, totalLeads: total, refreshedAt }, listCacheFilters);
            setRows(mapped); setTotalLeads(total); setLastRefreshAt(refreshedAt); setLoadError(null);
        })();

        return () => { cancelled = true; };
    }, [page, limit, debouncedTextSearch, debouncedZipCodeSearch, listRefreshNonce, listCacheFilters, fetchRestaurantLeads, markRestaurantLeadsSatisfiedWithoutFetch]);

    // ── Auto-close call modal when call ends ────────────────────────────────────
    useEffect(() => {
        if (voice.status !== 'ended') return;
        const row = twilio_call_row_ref.current;
        if (!row) return;
        twilio_call_row_ref.current = null;
        const call_sid = twilio_call_sid_ref.current;
        twilio_call_sid_ref.current = null;
        const current_status = parseStatusValueForApi(row?.currentStatus);
        if (current_status === 'not_onboarded_yet') {
            applyMarketingUpdate(row, {}, 'call completed')
                .then((result) => {
                    if (result.ok) {
                        patchRowLocal(row.id, { ...(call_sid ? { lastCallSid: call_sid } : {}) });
                        setListRefreshNonce((n) => n + 1);
                    }
                });
        }
    }, [voice.status]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Handlers ─────────────────────────────────────────────────────────────────

    const handleRefreshList = () => {
        invalidateMarketingLeadPageCache(page, limit, listCacheFilters);
        setListRefreshNonce((n) => n + 1);
    };

    const patchRowLocal = (leadId, changes = {}) => {
        if (!leadId) return;
        setRows((prev) => prev.map((row) => (row.id === leadId ? { ...row, ...changes } : row)));
    };

    const applyMarketingUpdate = async (row, updates, reasonText) => {
        const res = await updateRestaurantLeadByMarketingAPI({
            _id: row?.id,
            place_id: row?.placeId,
            ...updates,
            reason: reasonText,
            api_name: 'MarketingLead UI',
        });
        if (res?.hasError) {
            return { ok: false, message: res.message || 'Failed to update lead.' };
        }
        const payload = res?.data || {};
        if (payload?.success !== true) {
            return {
                ok: false,
                message: formatMarketingUpdateError(payload?.error || payload?.message || 'Could not update lead.'),
            };
        }
        return { ok: true };
    };

    const openActionModal = (type, row) => {
        setActionModal({ open: true, type, row });
        if (type === 'call') {
            twilio_call_row_ref.current = row;
            voice.clear_error();
            voice.prepare();
        }
    };

    const closeActionModal = () => {
        if (voice.is_calling || voice.is_in_call) voice.hangup();
        twilio_call_row_ref.current = null;
        setActionModal({ open: false, type: 'call', row: null });
    };

    const toggleLogsRow = (rowId) => {
        setExpandedRowId((prev) => (prev === rowId ? null : rowId));
        setAccordionTab(0);
    };

    const openRowMenu = (row) => (event) => setRowMenu({ anchorEl: event.currentTarget, row });
    const closeRowMenu = () => setRowMenu({ anchorEl: null, row: null });

    const openUpdateLeadModal = (row) => {
        setUpdateModal({
            open: true, row,
            currentStatus: parseStatusValueForApi(row?.currentStatus),
            marketingNotes: String(row?.marketingNotes ?? ''),
            smsSent: Boolean(row?.smsSent),
            submitting: false, error: '',
        });
    };

    const closeUpdateLeadModal = () => {
        setUpdateModal({
            open: false, row: null,
            currentStatus: 'not_onboarded_yet',
            marketingNotes: '', smsSent: false,
            submitting: false, error: '',
        });
    };

    const handleUpdateLeadSubmit = async () => {
        const row = updateModal.row;
        if (!row) return;
        setUpdateModal((prev) => ({ ...prev, submitting: true, error: '' }));
        const result = await applyMarketingUpdate(
            row,
            {
                current_status: updateModal.currentStatus,
                marketing_notes: updateModal.marketingNotes,
                sms_sent: updateModal.smsSent,
            },
            'lead update from modal'
        );
        if (!result.ok) {
            setUpdateModal((prev) => ({ ...prev, submitting: false, error: result.message }));
            return;
        }
        patchRowLocal(row.id, {
            currentStatus:
                CURRENT_STATUS_OPTIONS.find((opt) => opt.value === updateModal.currentStatus)?.label ||
                row.currentStatus,
            smsSent: updateModal.smsSent,
            marketingNotes: updateModal.marketingNotes,
        });
        handleRefreshList();
        setUpdateModal((prev) => ({ ...prev, submitting: false }));
        closeUpdateLeadModal();
    };

    const handleSmsConfirm = async (row) => {
        if (!row) return;
        const result = await applyMarketingUpdate(row, { sms_sent: true }, 'sms action confirmed');
        if (result.ok) patchRowLocal(row.id, { smsSent: true });
    };

    const handleSaveCallOutcome = async (row, { outcome, notes, meetingAt }) => {
        if (!row || !outcome) return;

        // Read logged-in user info for audit fields
        const loginInfo = (() => {
            try { return JSON.parse(localStorage.getItem('loginInfo') ?? '{}'); }
            catch (_) { return {}; }
        })();

        const updates = {
            call_status: outcome,
            ...(notes?.trim() && { calling_note: notes.trim() }),
            ...(outcome === 'meeting_scheduled' && meetingAt && { meeting_scheduled_at: meetingAt }),
            ...(loginInfo?._id && { changed_by_id: String(loginInfo._id) }),
            ...(loginInfo?.name && { changed_by_name: String(loginInfo.name) }),
            ...(loginInfo?.role && { changed_by_role: String(loginInfo.role) }),
        };

        const result = await applyMarketingUpdate(row, updates, 'call status update');

        if (result.ok) {
            // Patch local row: reflect the new status for meeting_scheduled
            const localPatch = {};
            if (outcome === 'meeting_scheduled') {
                localPatch.currentStatus = 'Meeting scheduled';
                if (meetingAt) localPatch.meeting_scheduled_at = String(meetingAt);
            }
            if (Object.keys(localPatch).length) patchRowLocal(row.id, localPatch);
            setListRefreshNonce((n) => n + 1);
        }
    };

    const handleBulkZipSubmit = async () => {
        const zip = bulkContactModal.zip.trim();
        if (!zip) {
            setBulkContactModal((prev) => ({ ...prev, error: 'ZIP code is required.' }));
            return;
        }
        setBulkContactModal((prev) => ({ ...prev, submitting: true, error: '' }));

        const res = await bulkUpdateShouldContactByZipcodeAPI({
            zip,
            should_contact: bulkContactModal.shouldContact,
        });

        if (res?.hasError) {
            setBulkContactModal((prev) => ({ ...prev, submitting: false, error: res.message || 'Failed to update leads.' }));
            return;
        }

        const payload = res?.data || {};
        if (payload?.success !== true) {
            setBulkContactModal((prev) => ({
                ...prev, submitting: false,
                error: payload?.error || payload?.message || 'Could not update leads.',
            }));
            return;
        }

        setBulkContactModal((prev) => ({
            ...prev, submitting: false,
            result: {
                zip: payload.zip ?? zip,
                should_contact: payload.should_contact ?? bulkContactModal.shouldContact,
                matched_count: payload.matched_count,
                modified_count: payload.modified_count,
            },
        }));
        handleRefreshList();
    };

    // ── Render ───────────────────────────────────────────────────────────────────

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '100vh',
                bgcolor: '#F5F4F2',
                py: { xs: 3, sm: 4 },
            }}
        >
            {/* ── Page header ─────────────────────────────────────────────── */}
            <Box
                sx={{
                    maxWidth: 1320, mx: 'auto', mb: 3,
                    display: 'flex', alignItems: 'flex-end',
                    justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontFamily: 'Inter Bold, sans-serif',
                            fontSize: { xs: 22, sm: 28 },
                            fontWeight: 700, color: '#1A1D23',
                            letterSpacing: '-0.03em', lineHeight: 1.15,
                        }}
                    >
                        Marketing Leads
                    </Typography>
                    <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: '#6B7280', mt: 0.5 }}>
                        Restaurant outreach pipeline · server-side paginated
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {/* Total badge */}
                    <Box
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 1,
                            bgcolor: COLORS.white, border: `1px solid ${COLORS.border}`,
                            borderRadius: '10px', px: 1.75, py: 0.75,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                        }}
                    >
                        <CampaignOutlinedIcon sx={{ fontSize: 16, color: '#8C8C8C' }} />
                        <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: '#8C8C8C' }}>
                            Total
                        </Typography>
                        <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 15, fontWeight: 700, color: '#111111' }}>
                            {totalLeads}
                        </Typography>
                    </Box>

                    {/* Bulk ZIP button */}
                    <Button
                        onClick={() =>
                            setBulkContactModal({ open: true, zip: '', shouldContact: true, submitting: false, error: '', result: null })
                        }
                        startIcon={<TuneRoundedIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            height: 38, borderRadius: '10px', border: '1px solid #E8E8E8',
                            fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 13,
                            color: '#111111', textTransform: 'none', px: 2,
                            bgcolor: '#FFFFFF', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                            whiteSpace: 'nowrap', transition: 'all 0.18s ease',
                            '&:hover': {
                                bgcolor: '#111111', color: '#FFFFFF', borderColor: '#111111',
                                boxShadow: '0 3px 10px rgba(0,0,0,0.18)',
                                '& .MuiButton-startIcon': { color: '#FFFFFF' },
                            },
                        }}
                    >
                        Bulk ZIP Update
                    </Button>

                    {/* Refresh button */}
                    <Tooltip title={`Last refresh: ${formatLastRefresh(lastRefreshAt)}`} placement='bottom'>
                        <span>
                            <IconButton
                                disabled={loading}
                                onClick={handleRefreshList}
                                sx={{
                                    width: 38, height: 38, borderRadius: '10px',
                                    bgcolor: '#FFFFFF', border: '1px solid #E8E8E8',
                                    color: '#8C8C8C', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                                    transition: 'all 0.18s ease',
                                    '&:hover': { bgcolor: '#111111', color: '#FFFFFF', borderColor: '#111111', boxShadow: '0 3px 10px rgba(0,0,0,0.18)' },
                                    '&.Mui-disabled': { color: '#D4D4D4', bgcolor: '#FAFAFA' },
                                }}
                            >
                                <RefreshOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {/* ── Main card ────────────────────────────────────────────────── */}
            <Box
                sx={{
                    maxWidth: 1320, mx: 'auto',
                    bgcolor: '#FFFFFF', borderRadius: '16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                }}
            >
                {/* ── Status tab row ─────────────────────────────────────────── */}
                <Box
                    sx={{
                        px: { xs: 2, sm: 3.5 }, pt: 2, pb: 0,
                        borderBottom: `1px solid ${COLORS.border}`,
                        display: 'flex', alignItems: 'flex-end', gap: 0,
                        overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' },
                    }}
                >
                    {STATUS_TAB_OPTIONS.map((tab) => {
                        const active = statusFilter === tab.value;
                        return (
                            <Box
                                key={tab.value}
                                onClick={() => setStatusFilter(tab.value)}
                                sx={{
                                    px: 2, py: 1.25, cursor: 'pointer', position: 'relative',
                                    fontFamily: active ? 'Inter Bold, sans-serif' : 'Inter Regular, sans-serif',
                                    fontWeight: active ? 600 : 400, fontSize: 13,
                                    color: active ? '#111111' : '#8C8C8C', whiteSpace: 'nowrap',
                                    borderBottom: active ? '2px solid #111111' : '2px solid transparent',
                                    mb: '-1px', transition: 'all 0.15s ease',
                                    '&:hover': { color: '#111111' },
                                }}
                            >
                                {tab.label}
                            </Box>
                        );
                    })}
                </Box>

                {/* ── Toolbar row ──────────────────────────────────────────────── */}
                <Box
                    sx={{
                        px: { xs: 2, sm: 3.5 }, py: 2,
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
                        borderBottom: '1px solid #F0F0F0', bgcolor: '#FAFAFA',
                    }}
                >
                    {/* Left: search inputs */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: '1 1 380px' }}>
                        <TextField
                            size='small'
                            placeholder='Search restaurants or addresses…'
                            value={textSearch}
                            onChange={(e) => setTextSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SearchIcon sx={{ color: COLORS.muted, fontSize: 17 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: textSearch ? (
                                    <InputAdornment position='end'>
                                        <IconButton size='small' onClick={() => setTextSearch('')}>
                                            <CancelRounded sx={{ fontSize: 15, color: COLORS.muted }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                            sx={{
                                flex: '1 1 220px', maxWidth: 340,
                                '& .MuiOutlinedInput-root': {
                                    height: 38, borderRadius: '9px',
                                    bgcolor: COLORS.white, fontSize: 13,
                                    fontFamily: 'Inter Regular, sans-serif',
                                    '& fieldset': { borderColor: COLORS.border },
                                    '&:hover fieldset': { borderColor: '#B0B8CC' },
                                    '&.Mui-focused fieldset': { borderColor: COLORS.ink, borderWidth: '1px' },
                                },
                            }}
                        />
                        <TextField
                            size='small'
                            placeholder='Zip code'
                            value={zipCodeSearch}
                            onChange={(e) => setZipCodeSearch(e.target.value)}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 10 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SearchIcon sx={{ color: COLORS.muted, fontSize: 17 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: zipCodeSearch ? (
                                    <InputAdornment position='end'>
                                        <IconButton size='small' onClick={() => setZipCodeSearch('')}>
                                            <CancelRounded sx={{ fontSize: 15, color: COLORS.muted }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                            sx={{
                                width: 140,
                                '& .MuiOutlinedInput-root': {
                                    height: 38, borderRadius: '9px',
                                    bgcolor: COLORS.white, fontSize: 13,
                                    fontFamily: 'Inter Regular, sans-serif',
                                    '& fieldset': { borderColor: COLORS.border },
                                    '&:hover fieldset': { borderColor: '#B0B8CC' },
                                    '&.Mui-focused fieldset': { borderColor: COLORS.ink, borderWidth: '1px' },
                                },
                            }}
                        />
                    </Box>

                    {/* Right: filter dropdowns */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <FormControl size='small' sx={{ minWidth: 130, ...outlinedSelectSx }}>
                            <InputLabel sx={{ fontSize: 13 }}>SMS Sent</InputLabel>
                            <Select
                                label='SMS Sent'
                                value={smsSentFilter}
                                onChange={(e) => setSmsSentFilter(e.target.value)}
                                MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                                sx={{ height: 38, borderRadius: '9px', fontSize: 13 }}
                            >
                                <MenuItem value='all' sx={menuItemSx}>All</MenuItem>
                                <MenuItem value='true' sx={menuItemSx}>Sent</MenuItem>
                                <MenuItem value='false' sx={menuItemSx}>Not sent</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size='small' sx={{ minWidth: 145, ...outlinedSelectSx }}>
                            <InputLabel sx={{ fontSize: 13 }}>Contact</InputLabel>
                            <Select
                                label='Contact'
                                value={shouldContactFilter}
                                onChange={(e) => setShouldContactFilter(e.target.value)}
                                MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                                sx={{ height: 38, borderRadius: '9px', fontSize: 13 }}
                            >
                                <MenuItem value='all' sx={menuItemSx}>All</MenuItem>
                                <MenuItem value='true' sx={menuItemSx}>Should contact</MenuItem>
                                <MenuItem value='false' sx={menuItemSx}>Skip</MenuItem>
                            </Select>
                        </FormControl>

                        {hasActiveFilters && (
                            <Button
                                size='small'
                                onClick={() => {
                                    setTextSearch('');
                                    setZipCodeSearch('');
                                    setStatusFilter('all');
                                    setSmsSentFilter('all');
                                    setShouldContactFilter('all');
                                }}
                                sx={{
                                    height: 38, borderRadius: '9px', border: `1px solid ${COLORS.border}`,
                                    fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: COLORS.muted,
                                    textTransform: 'none', px: 1.75, bgcolor: COLORS.white,
                                    '&:hover': { bgcolor: '#F3F4F6', borderColor: '#B0B8CC' },
                                }}
                            >
                                Clear filters
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* ── Error banner ─────────────────────────────────────────────── */}
                {loadError && (
                    <Box sx={{ px: { xs: 2, sm: 3.5 }, pt: 2 }}>
                        <Box
                            sx={{
                                p: 2, bgcolor: '#fee2e2', border: '1px solid #fca5a5',
                                borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1,
                            }}
                        >
                            <CancelRounded sx={{ fontSize: 18, color: '#dc2626', flexShrink: 0 }} />
                            <Typography sx={{ color: '#dc2626', fontFamily: 'Inter Regular, sans-serif', fontSize: 14 }}>
                                {loadError}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* ── Table ────────────────────────────────────────────────────── */}
                <TableContainer sx={{ bgcolor: COLORS.white }}>
                    <Table
                        size='medium'
                        sx={{
                            tableLayout: 'fixed', width: '100%',
                            borderCollapse: 'collapse', borderSpacing: 0,
                            '& .MuiTableCell-root': { boxSizing: 'border-box' },
                        }}
                    >
                        <colgroup>
                            <col style={{ width: '20%' }} />
                            <col style={{ width: '18%' }} />
                            <col style={{ width: '8%' }} />
                            <col style={{ width: '14%' }} />
                            <col style={{ width: '9%' }} />
                            <col style={{ width: '6%' }} />
                            <col style={{ width: '17%' }} />
                            <col style={{ width: '8%' }} />
                        </colgroup>

                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F7F8FA' }}>
                                {['Name', 'Address', 'Zipcode', 'Type', 'SMS', 'Should Contact', 'Status', 'Actions'].map((col, i) => (
                                    <TableCell
                                        key={col}
                                        align={col === 'Actions' ? 'center' : 'left'}
                                        sx={{
                                            fontFamily: 'Inter Bold, sans-serif', fontSize: 11, fontWeight: 600,
                                            letterSpacing: '0.03em', textTransform: 'uppercase', color: '#9CA3AF',
                                            bgcolor: '#F7F8FA', borderTop: 'none', borderLeft: 'none',
                                            borderRight: 'none', borderBottom: `1px solid ${COLORS.border}`,
                                            py: 1.5, px: i === 0 ? 3.5 : 2, whiteSpace: 'nowrap', overflow: 'hidden',
                                        }}
                                    >
                                        <Tooltip title={col} placement='top' arrow enterDelay={250}>
                                            <Box component='span' sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }}>
                                                {col}
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 10, border: 'none' }}>
                                        <CircularProgress size={26} sx={{ color: '#111111' }} />
                                        <Typography sx={{ mt: 1.5, fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: COLORS.muted }}>
                                            Loading leads…
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                            {!loading && !loadError && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 10, border: 'none' }}>
                                        <Box
                                            sx={{
                                                width: 56, height: 56, borderRadius: '16px',
                                                bgcolor: '#F7F8FA', border: `1px solid ${COLORS.border}`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                mx: 'auto', mb: 2,
                                            }}
                                        >
                                            <CampaignOutlinedIcon sx={{ fontSize: 26, color: COLORS.muted }} />
                                        </Box>
                                        <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontSize: 15, color: COLORS.ink, mb: 0.5 }}>
                                            No leads found
                                        </Typography>
                                        <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: COLORS.muted }}>
                                            Try adjusting the filters or refresh the list.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                            {!loading && rows.map((row) => (
                                <React.Fragment key={row.id}>
                                    {/* ── Data row ── */}
                                    <TableRow
                                        hover
                                        sx={{
                                            transition: 'background-color 0.12s ease',
                                            '&:hover': { bgcolor: '#F9FAFB !important' },
                                            '&:last-child td': { borderBottom: 'none' },
                                        }}
                                    >
                                        {/* Name — click to toggle logs */}
                                        <TableCell
                                            onClick={() => toggleLogsRow(row.id)}
                                            sx={{
                                                borderTop: 'none',
                                                borderLeft: `3px solid ${expandedRowId === row.id ? '#111111' : 'transparent'}`,
                                                borderRight: 'none', borderBottom: '1px solid #F3F4F6',
                                                py: 1.75, px: 2, pl: 3,
                                                cursor: 'pointer', userSelect: 'none',
                                                transition: 'border-left-color 0.12s ease',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box
                                                    sx={{
                                                        width: 34, height: 34, borderRadius: '10px',
                                                        bgcolor: expandedRowId === row.id ? '#111111' : '#F3F4F6',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        flexShrink: 0, transition: 'all 0.12s ease',
                                                    }}
                                                >
                                                    <CampaignOutlinedIcon
                                                        sx={{ fontSize: 15, color: expandedRowId === row.id ? '#FFFFFF' : '#9CA3AF' }}
                                                    />
                                                </Box>
                                                <Box sx={{ overflow: 'hidden' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Tooltip title={row.name} placement='top' enterDelay={400} arrow>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: 'Inter Bold, sans-serif', fontWeight: 600,
                                                                    fontSize: 13.5, color: '#111827', lineHeight: 1.3,
                                                                    overflow: 'hidden', whiteSpace: 'nowrap',
                                                                    textOverflow: 'ellipsis', maxWidth: 180,
                                                                }}
                                                            >
                                                                {row.name}
                                                            </Typography>
                                                        </Tooltip>
                                                        {expandedRowId === row.id
                                                            ? <KeyboardArrowUpRoundedIcon sx={{ fontSize: 14, color: '#111111', flexShrink: 0 }} />
                                                            : <KeyboardArrowDownRoundedIcon sx={{ fontSize: 14, color: '#C4C9D4', flexShrink: 0 }} />
                                                        }
                                                    </Box>
                                                    <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11.5, color: '#9CA3AF', mt: 0.1 }}>
                                                        {row.phone || '—'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>

                                        {/* Address */}
                                        <TableCell sx={{ borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderBottom: '1px solid #F3F4F6', py: 1.75, px: 2, overflow: 'hidden' }}>
                                            <AddressCell text={row.address} />
                                        </TableCell>

                                        {/* Zipcode */}
                                        <TableCell sx={{ borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderBottom: '1px solid #F3F4F6', py: 1.75, px: 2 }}>
                                            <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#000' }}>{row.zip}</Typography>
                                        </TableCell>

                                        {/* Type */}
                                        <TableCell sx={{ borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderBottom: '1px solid #F3F4F6', py: 1.75, px: 2, overflow: 'hidden' }}>
                                            <Tooltip
                                                title={row.types.map((t) => {
                                                    const f = String(t ?? '').replace(/_/g, ' ');
                                                    return f ? f.charAt(0).toUpperCase() + f.slice(1) : '';
                                                }).join(', ')}
                                                placement='top' enterDelay={350} arrow
                                            >
                                                <Typography
                                                    sx={{
                                                        fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#111111',
                                                        maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap', cursor: 'default',
                                                    }}
                                                >
                                                    {row.types.map((t) => {
                                                        const f = String(t ?? '').replace(/_/g, ' ');
                                                        return f ? f.charAt(0).toUpperCase() + f.slice(1) : '';
                                                    }).join(', ')}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>

                                        {/* SMS Sent */}
                                        <TableCell sx={{ borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderBottom: '1px solid #F3F4F6', py: 1.75, px: 2 }}>
                                            {row.smsSent ? (
                                                <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, bgcolor: '#111111', color: '#FFFFFF', borderRadius: '20px', px: 1.25, py: 0.45, fontSize: 11.5, fontFamily: 'Inter Bold, sans-serif', fontWeight: 600 }}>
                                                    <CheckCircleRounded sx={{ fontSize: 12, color: '#FFFFFF' }} />
                                                    Sent
                                                </Box>
                                            ) : (
                                                <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: '#F3F4F6', color: '#9CA3AF', borderRadius: '20px', px: 1.1, py: 0.4, fontSize: 11.5, fontFamily: 'Inter Regular, sans-serif' }}>
                                                    No
                                                </Box>
                                            )}
                                        </TableCell>

                                        {/* Should Contact */}
                                        <TableCell sx={{ borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderBottom: '1px solid #F3F4F6', py: 1.75, px: 2 }}>
                                            {row.shouldContact ? (
                                                <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, bgcolor: '#111111', color: '#FFFFFF', borderRadius: '20px', px: 0.9, py: 0.35, fontSize: 11.5, fontFamily: 'Inter Bold, sans-serif', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                    Yes
                                                </Box>
                                            ) : (
                                                <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: '#F3F4F6', color: '#9CA3AF', borderRadius: '20px', px: 0.9, py: 0.35, fontSize: 11.5, fontFamily: 'Inter Regular, sans-serif', whiteSpace: 'nowrap' }}>
                                                    No
                                                </Box>
                                            )}
                                        </TableCell>

                                        {/* Current Status */}
                                        <TableCell sx={{ borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderBottom: '1px solid #F3F4F6', py: 1.75, px: 2, verticalAlign: 'top' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.35, minWidth: 0 }}>
                                                <StatusBadge status={row.currentStatus} />
                                                {String(row.meeting_scheduled_at ?? '').trim() ? (
                                                    <Typography
                                                        sx={{
                                                            fontFamily: 'Inter Regular, sans-serif',
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            color: '#B45309',
                                                            lineHeight: 1.35,
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {formatLogTime(row.meeting_scheduled_at)}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        </TableCell>

                                        {/* Actions */}
                                        <TableCell align='center' sx={{ borderTop: 'none', borderLeft: 'none', borderRight: 'none', borderBottom: '1px solid #F3F4F6', py: 1.75, px: 2 }}>
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                                                {row.shouldContact && (
                                                    <>
                                                        <Tooltip title={`Call ${row.phone}`} placement='top'>
                                                            <IconButton
                                                                size='small'
                                                                onClick={() => openActionModal('call', row)}
                                                                sx={{
                                                                    width: 30, height: 30, borderRadius: '8px',
                                                                    color: '#374151', bgcolor: '#F3F4F6', border: '1px solid #E8E8E8',
                                                                    transition: 'all 0.15s ease',
                                                                    '&:hover': { bgcolor: '#111111', color: '#FFFFFF', borderColor: '#111111', boxShadow: '0 2px 8px rgba(0,0,0,0.18)' },
                                                                }}
                                                            >
                                                                <PhoneInTalkRoundedIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title='Send SMS' placement='top'>
                                                            <IconButton
                                                                size='small'
                                                                onClick={() => openActionModal('sms', row)}
                                                                sx={{
                                                                    width: 30, height: 30,
                                                                    borderRadius: '8px',
                                                                    color: '#374151',
                                                                    bgcolor: '#F3F4F6',
                                                                    border: '1px solid #E8E8E8',
                                                                    transition: 'all 0.15s ease',
                                                                    '&:hover': {
                                                                        bgcolor: '#111111',
                                                                        color: '#FFFFFF',
                                                                        borderColor: '#111111',
                                                                    },
                                                                }}
                                                            >
                                                                <SmsRoundedIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                                <Tooltip title='More actions' placement='top'>
                                                    <IconButton
                                                        size='small'
                                                        onClick={openRowMenu(row)}
                                                        sx={{
                                                            width: 30, height: 30, borderRadius: '8px', color: '#9CA3AF',
                                                            transition: 'all 0.15s ease',
                                                            '&:hover': { bgcolor: '#F3F4F6', color: '#111111' },
                                                        }}
                                                    >
                                                        <MoreHorizIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>

                                    {/* ── Logs accordion row ── */}
                                    <TableRow sx={{ bgcolor: 'transparent' }}>
                                        <TableCell
                                            colSpan={8}
                                            sx={{
                                                p: 0,
                                                borderBottom: expandedRowId === row.id ? `1px solid ${COLORS.border}` : 'none',
                                                borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                                            }}
                                        >
                                            <Collapse in={expandedRowId === row.id} timeout='auto' unmountOnExit>
                                                <Box sx={{ bgcolor: '#FAFAFA', borderTop: '1px solid #F0F0F0' }}>
                                                    {/* Tab bar */}
                                                    <Box
                                                        sx={{
                                                            px: { xs: 2, sm: 4 }, pt: 2, pb: 0,
                                                            display: 'flex', alignItems: 'center', gap: 2,
                                                            borderBottom: '1px solid #EFEFEF',
                                                        }}
                                                    >
                                                        <Tabs
                                                            value={accordionTab}
                                                            onChange={(_, v) => setAccordionTab(v)}
                                                            sx={{
                                                                minHeight: 36,
                                                                '& .MuiTabs-indicator': { bgcolor: '#111111', height: 2, borderRadius: '2px 2px 0 0' },
                                                                '& .MuiTab-root': {
                                                                    minHeight: 36, py: 0, px: 0, mr: 3,
                                                                    fontFamily: 'Inter Regular, sans-serif', fontSize: 13,
                                                                    color: '#9CA3AF', textTransform: 'none', fontWeight: 400,
                                                                    transition: 'color 0.15s',
                                                                    '&.Mui-selected': { color: '#111111', fontFamily: 'Inter Bold, sans-serif', fontWeight: 700 },
                                                                },
                                                            }}
                                                        >
                                                            <Tab
                                                                label={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                        <HistoryRoundedIcon sx={{ fontSize: 14 }} />
                                                                        <span>Status Logs</span>
                                                                        <Box
                                                                            component='span'
                                                                            sx={{
                                                                                px: 0.85, py: 0.1, borderRadius: '5px',
                                                                                bgcolor: accordionTab === 0 ? '#111111' : '#F3F4F6',
                                                                                color: accordionTab === 0 ? '#FFFFFF' : '#6B7280',
                                                                                fontFamily: 'Inter Bold, sans-serif',
                                                                                fontSize: 10.5, fontWeight: 700, transition: 'all 0.15s',
                                                                            }}
                                                                        >
                                                                            {row.logs.length}
                                                                        </Box>
                                                                    </Box>
                                                                }
                                                                disableRipple
                                                            />
                                                            <Tab
                                                                label={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                        <PhoneInTalkRoundedIcon sx={{ fontSize: 14 }} />
                                                                        <span>Call Recording</span>
                                                                    </Box>
                                                                }
                                                                disableRipple
                                                            />
                                                        </Tabs>
                                                    </Box>

                                                    {/* Tab panels */}
                                                    <Box sx={{ px: { xs: 2, sm: 4 }, py: 2.5 }}>
                                                        {accordionTab === 0 && (
                                                            <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
                                                                <StatusLogsTable logs={row.logs} />
                                                            </Box>
                                                        )}
                                                        {accordionTab === 1 && (
                                                            <CallRecordingPanel
                                                                phone={normalizeToE164(row.phone)}
                                                                callSid={row.lastCallSid ?? null}
                                                                customerName={row.name}
                                                                canDeleteRecording={canDeleteRecording}
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>

                    {/* Pagination */}
                    <TablePagination
                        component='div'
                        count={totalLeads}
                        page={Math.max(0, page - 1)}
                        onPageChange={(_, nextZero) => setPage(nextZero + 1)}
                        rowsPerPage={limit}
                        onRowsPerPageChange={(e) => {
                            setLimit(Math.min(200, Math.max(1, parseInt(e.target.value, 10) || 20)));
                            setPage(1);
                        }}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        labelRowsPerPage='Rows per page'
                        sx={{
                            borderTop: '1px solid #F0F0F0', bgcolor: '#FAFAFA',
                            fontFamily: 'Inter Regular, sans-serif', color: '#8C8C8C',
                            '& .MuiTablePagination-toolbar': { minHeight: 50, px: 3 },
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontSize: 13, color: '#8C8C8C', fontFamily: 'Inter Regular, sans-serif',
                            },
                            '& .MuiIconButton-root': { color: '#8C8C8C', borderRadius: '8px', transition: 'all 0.15s ease' },
                            '& .MuiIconButton-root:hover': { bgcolor: '#F3F4F6', color: '#111111' },
                        }}
                    />
                </TableContainer>
            </Box>

            {/* ── Row context menu ──────────────────────────────────────────── */}
            <Menu
                anchorEl={rowMenu.anchorEl}
                open={Boolean(rowMenu.anchorEl)}
                onClose={closeRowMenu}
                PaperProps={{
                    sx: {
                        border: '1px solid #E8E8E8', borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.05), 0 10px 30px rgba(0,0,0,0.10)',
                        minWidth: 176, py: 0.5,
                    },
                }}
            >
                <MenuItem
                    onClick={() => { const row = rowMenu.row; closeRowMenu(); if (row) openUpdateLeadModal(row); }}
                    sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, gap: 1, color: '#111111', borderRadius: '6px', mx: 0.5, '&:hover': { bgcolor: '#F3F4F6' } }}
                >
                    Update Lead
                </MenuItem>
                <Divider sx={{ my: 0.5, borderColor: '#F0F0F0' }} />
                <MenuItem
                    onClick={() => { const row = rowMenu.row; closeRowMenu(); if (row) toggleLogsRow(row.id); }}
                    sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, gap: 1, color: '#111111', borderRadius: '6px', mx: 0.5, '&:hover': { bgcolor: '#F3F4F6' } }}
                >
                    <HistoryRoundedIcon sx={{ fontSize: 16, color: '#8C8C8C' }} />
                    Show Logs
                </MenuItem>
            </Menu>

            {/* ── Modals ───────────────────────────────────────────────────── */}
            <UpdateLeadModal
                modal={updateModal}
                onChange={setUpdateModal}
                onClose={closeUpdateLeadModal}
                onSubmit={handleUpdateLeadSubmit}
            />

            <CallSmsModal
                modal={actionModal}
                voice={voice}
                onClose={closeActionModal}
                onSmsConfirm={handleSmsConfirm}
                onSaveCallOutcome={handleSaveCallOutcome}
            />

            <BulkZipModal
                modal={bulkContactModal}
                onChange={setBulkContactModal}
                onClose={() => setBulkContactModal({ open: false, zip: '', shouldContact: true, submitting: false, error: '', result: null })}
                onSubmit={handleBulkZipSubmit}
            />
        </Box>
    );
};

export default MarketingLead;
